/**
 * Grid-view card components — one per lifecycle stage.
 * All cards accept BoardOrder (real API type) from orders.api.ts.
 */
import { Check, MoreHorizontal, Pause, Play, Phone, Printer, Navigation, Timer, MapPin, Bell, Loader2, Utensils, RotateCcw } from 'lucide-react';
import { Trans, Plural } from '@lingui/react/macro';
import { useLingui } from '@lingui/react/macro';
import { Button } from '@/components/ui/button';
import { elapsedPct, formatElapsed, useTicketElapsed } from '@/lib/elapsed';
import type { BoardOrder } from '@/lib/api/services/orders.api';
import {
  ProgressRing, StatePill, PrioPill, TimerChip, ChefAvatar, deriveBoardState,
} from './OrderPrimitives';

// ─── Outline icon button ──────────────────────────────────────────────────────

function OutlineIconBtn({ title, onClick, children }: { title: string; onClick?: () => void; children: React.ReactNode }) {
  return (
    <Button
      variant="outline"
      size="icon"
      title={title}
      onClick={onClick}
      className="h-11 w-11 shrink-0 rounded-xl border-(--color-border) bg-(--color-surface-content) hover:bg-(--color-surface-muted) [&_svg]:size-5"
    >
      {children}
    </Button>
  );
}

// ─── Cooking card ─────────────────────────────────────────────────────────────

export function CookingCard({
  order, onDismiss, completePending, canComplete = true,
}: {
  order: BoardOrder;
  onDismiss: (id: number) => void;
  /** true while the complete API call is in flight */
  completePending?: boolean;
  canComplete?: boolean;
}) {
  const { t } = useLingui();
  const state = deriveBoardState(order, 'cooking');
  const elapsedSeconds = useTicketElapsed(order);
  const tableLabel = order.table
    ? `${order.table.section} ${order.table.name}`
    : order.guestName ?? '—';

  return (
    <article className="bg-(--color-surface-muted) rounded-2xl px-3 pt-3 pb-4 flex flex-col gap-2.5">
      {/* Head: ring + meta */}
      <div className="flex items-start gap-3">
        <ProgressRing pct={elapsedPct(elapsedSeconds, order.targetMinutes)} state={state} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap mb-1.5">
            <TimerChip label={`${formatElapsed(elapsedSeconds)} / ${order.targetMinutes}m`} />
            <StatePill state={state} />
            <PrioPill prio={order.priority} />
          </div>
          <div className="text-[1rem] font-bold text-(--color-text-primary) truncate">{order.title}</div>
          <div className="text-[0.78125rem] text-(--color-text-secondary) mt-0.5">
            {order.tkt} · {tableLabel}
            {order.guestCount != null && (
              <> · <Plural value={order.guestCount} one="# guest" other="# guests" /></>
            )}
          </div>
        </div>
      </div>

      {/* Data rows */}
      {order.allergens.length > 0 && (
        <div className="flex items-center justify-between text-[0.8125rem]">
          <span className="text-(--color-text-secondary)"><Trans>Allergens</Trans></span>
          <span className="font-semibold">{order.allergens.join(', ')}</span>
        </div>
      )}
      <div className="flex items-center justify-between text-[0.8125rem]">
        <span className="text-(--color-text-secondary)"><Trans>Chef</Trans></span>
        {order.chefName ? (
          <span className="flex items-center gap-1.5">
            <ChefAvatar name={order.chefName} className="w-5 h-5" />
            {order.chefName}
          </span>
        ) : (
          <span className="text-(--color-text-secondary) italic text-[0.75rem]"><Trans>Unassigned</Trans></span>
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center gap-1.5 pt-1.5 border-t border-dashed border-(--color-border)">
        <OutlineIconBtn title={t`More`}><MoreHorizontal /></OutlineIconBtn>
        <OutlineIconBtn title={order.isPaused ? t`Resume` : t`Pause`}>
          {order.isPaused ? <Play /> : <Pause />}
        </OutlineIconBtn>
        {canComplete && (
          <Button
            onClick={() => onDismiss(order.id)}
            disabled={completePending}
            className="flex-1 h-11 rounded-xl bg-(--color-success) hover:bg-(--color-success-hover) text-(--brand-white) gap-1.5 font-bold text-[0.8125rem] disabled:opacity-70"
          >
            {completePending
              ? <Loader2 className="h-4 w-4 animate-spin" />
              : <Check className="h-4 w-4" />}
            <Trans>Complete</Trans>
          </Button>
        )}
      </div>
    </article>
  );
}

// ─── Plating card ─────────────────────────────────────────────────────────────

export function PlatingCard({
  order, onPass, onSendBack, canPass = true,
}: {
  order: BoardOrder;
  onPass: (id: number) => void;
  onSendBack?: (id: number) => void;
  canPass?: boolean;
}) {
  const { t } = useLingui();
  const tableLabel = order.table
    ? `${order.table.section} ${order.table.name}`
    : '—';

  return (
    <article
      className="bg-(--color-surface-muted) rounded-2xl px-3 pt-3 pb-4 flex flex-col gap-2.5 border border-(--color-order-plating-bg)"
    >
      <div className="flex items-start gap-3">
        {/* Utensils icon block */}
        <div className="w-13 h-13 rounded-[0.6rem] bg-(--color-order-plating-bg) text-(--color-order-plating-fg) flex items-center justify-center shrink-0">
          <Utensils className="w-6 h-6" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap mb-1.5">
            <TimerChip icon={Utensils} label={t`On pass`} />
            <StatePill state="plating" />
            <PrioPill prio={order.priority} />
          </div>
          <div className="text-[1rem] font-bold text-(--color-text-primary) truncate">{order.title}</div>
          <div className="text-[0.78125rem] text-(--color-text-secondary) mt-0.5">
            {order.tkt} · {tableLabel} · <Trans>expo waiting</Trans>
          </div>
        </div>
      </div>

      {order.allergens.length > 0 && (
        <div className="flex items-center justify-between text-[0.8125rem]">
          <span className="text-(--color-text-secondary)"><Trans>Allergens</Trans></span>
          <span className="font-semibold">{order.allergens.join(', ')}</span>
        </div>
      )}
      {order.chefName && (
        <div className="flex items-center justify-between text-[0.8125rem]">
          <span className="text-(--color-text-secondary)"><Trans>Chef</Trans></span>
          <span className="flex items-center gap-1.5">
            <ChefAvatar name={order.chefName} className="w-5 h-5" />
            {order.chefName}
          </span>
        </div>
      )}

      <div className="flex items-center gap-1.5 pt-1.5 border-t border-dashed border-(--color-order-plating-bg)">
        <OutlineIconBtn title={t`More`}><MoreHorizontal /></OutlineIconBtn>
        {onSendBack && (
          <OutlineIconBtn title={t`Send back to cooking`} onClick={() => onSendBack(order.id)}>
            <RotateCcw />
          </OutlineIconBtn>
        )}
        {canPass && (
          <Button
            onClick={() => onPass(order.id)}
            className="flex-1 h-11 rounded-xl bg-(--color-order-plating-fg) hover:bg-(--color-order-plating-fg)/90 text-(--brand-white) gap-1.5 font-bold text-[0.8125rem]"
          >
            <Bell className="h-4 w-4" />
            <Trans>Mark Ready</Trans>
          </Button>
        )}
      </div>
    </article>
  );
}

// ─── Pickup card ──────────────────────────────────────────────────────────────

export function PickupCard({
  order, onDismiss,
}: {
  order: BoardOrder;
  onDismiss: (id: number) => void;
}) {
  const { t } = useLingui();
  const state = deriveBoardState(order, 'pickup');
  const tableLabel = order.table
    ? `${order.table.section} ${order.table.name}`
    : '—';

  return (
    <article className="bg-(--color-surface-muted) rounded-2xl px-3 pt-3 pb-4 flex flex-col gap-2.5">
      <div className="flex items-start gap-3">
        {/* Bell icon block */}
        <div className="w-13 h-13 rounded-[0.6rem] bg-(--color-order-pickup-bg) text-(--color-order-pickup-fg) flex items-center justify-center shrink-0">
          <Bell className="w-6 h-6" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap mb-1.5">
            <TimerChip icon={Timer} label={t`Ready`} />
            <StatePill state={state} />
            <PrioPill prio={order.priority} />
          </div>
          <div className="text-[1rem] font-bold text-(--color-text-primary) truncate">{order.title}</div>
          <div className="text-[0.78125rem] text-(--color-text-secondary) mt-0.5">
            {order.tkt} · {tableLabel}
          </div>
        </div>
      </div>

      {order.guestName && (
        <div className="flex items-center justify-between text-[0.8125rem]">
          <span className="text-(--color-text-secondary)"><Trans>Customer</Trans></span>
          <span className="font-semibold truncate">{order.guestName}</span>
        </div>
      )}
      {order.phone && (
        <div className="flex items-center justify-between text-[0.8125rem]">
          <span className="text-(--color-text-secondary)"><Trans>Phone</Trans></span>
          <span className="font-mono text-[0.78125rem]">{order.phone}</span>
        </div>
      )}
      {order.allergens.length > 0 && (
        <div className="flex items-center justify-between text-[0.8125rem]">
          <span className="text-(--color-text-secondary)"><Trans>Allergens</Trans></span>
          <span className="font-semibold">{order.allergens.join(', ')}</span>
        </div>
      )}

      <div className="flex items-center gap-1.5 pt-1.5 border-t border-dashed border-(--color-border)">
        {order.phone && <OutlineIconBtn title={t`Call`}><Phone /></OutlineIconBtn>}
        <OutlineIconBtn title={t`Print`}><Printer /></OutlineIconBtn>
        <Button
          onClick={() => onDismiss(order.id)}
          className="flex-1 h-11 rounded-xl bg-(--brand-blue) hover:bg-(--brand-blue)/90 text-(--brand-white) gap-1.5 font-bold text-[0.8125rem]"
        >
          <Check className="h-4 w-4" />
          <Trans>Hand off</Trans>
        </Button>
      </div>
    </article>
  );
}

// ─── Delivery card ────────────────────────────────────────────────────────────

export function DeliveryCard({
  order, onDismiss,
}: {
  order: BoardOrder;
  onDismiss: (id: number) => void;
}) {
  const { t } = useLingui();
  const state = deriveBoardState(order, 'delivery');

  return (
    <article className="bg-(--color-surface-muted) rounded-2xl px-3 pt-3 pb-4 flex flex-col gap-2.5">
      <div className="flex items-start gap-3">
        <div className="w-13 h-13 rounded-[0.6rem] bg-(--color-order-cooking-bg) text-(--color-order-cooking-fg) flex items-center justify-center shrink-0">
          <Navigation className="w-6 h-6" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap mb-1.5">
            <TimerChip icon={MapPin} label={t`En route`} />
            <StatePill state={state} />
            <PrioPill prio={order.priority} />
          </div>
          <div className="text-[1rem] font-bold text-(--color-text-primary) truncate">{order.title}</div>
          <div className="text-[0.78125rem] text-(--color-text-secondary) mt-0.5">{order.tkt}</div>
        </div>
      </div>

      {order.guestName && (
        <div className="flex items-center justify-between text-[0.8125rem]">
          <span className="text-(--color-text-secondary)"><Trans>Customer</Trans></span>
          <span className="font-semibold truncate">{order.guestName}</span>
        </div>
      )}
      {order.chefName && (
        <div className="flex items-center justify-between text-[0.8125rem]">
          <span className="text-(--color-text-secondary)"><Trans>Courier</Trans></span>
          <span className="flex items-center gap-1.5">
            <ChefAvatar name={order.chefName} className="w-5 h-5" />
            {order.chefName}
          </span>
        </div>
      )}

      <div className="flex items-center gap-1.5 pt-1.5 border-t border-dashed border-(--color-border)">
        <OutlineIconBtn title={t`Track`}><Navigation /></OutlineIconBtn>
        {order.phone && <OutlineIconBtn title={t`Call`}><Phone /></OutlineIconBtn>}
        <Button
          onClick={() => onDismiss(order.id)}
          className="flex-1 h-11 rounded-xl bg-(--color-text-primary) hover:bg-(--color-text-primary)/90 text-(--color-surface-page) gap-1.5 font-bold text-[0.8125rem]"
        >
          <Check className="h-4 w-4" />
          <Trans>Delivered</Trans>
        </Button>
      </div>
    </article>
  );
}

// ─── Inline styles helper ─────────────────────────────────────────────────────

/** Returns the correct grid columns template for each stage row layout */
export const ROW_GRID: Record<string, string> = {
  cooking:  '2.375rem 4rem minmax(13.75rem,1.5fr) 1fr 8.125rem 9.375rem auto',
  plating:  '2.375rem 4rem minmax(13.75rem,1.5fr) 1fr 9.375rem auto',
  pickup:   '2.375rem 4rem minmax(13.75rem,1.4fr) 1fr 7.5rem 9.375rem auto',
  delivery: '2.375rem 4rem minmax(13.75rem,1.4fr) 1.2fr 9.375rem 9.375rem auto',
};
